<template>
    <div>
        <page-header>
            Organizational Units
            <template v-slot:actions>
                <vx-button
                    @click="showOverview = !showOverview"
                    data-test="toggle-overview"
                    type="button"
                    color="muted-light"
                    size="sm"
                >
                    <span class="hidden sm:inline-block">{{ showOverview ? 'Hide' : 'Show' }} Overview</span>
                    <font-awesome-icon
                        widthAuto
                        class="inline-block! sm:hidden!"
                        :icon="faCircleQuestion"
                    ></font-awesome-icon>
                    <font-awesome-icon
                        widthAuto
                        class="hidden sm:inline-block"
                        :icon="showOverview ? faCircleChevronUp : faCircleChevronDown"
                    ></font-awesome-icon>
                </vx-button>
            </template>
        </page-header>

        <org-units-overview v-show="showOverview" class="mb-14"></org-units-overview>

        <org-unit-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            @created="handleCreated"
        ></org-unit-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                No Organizational Units yet.
                <vx-button v-if="isAdminOrOwner" @click="showCreateModal = true" data-test="create-ou-button">
                    Create OU
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </vx-box>
        </template>

        <div v-else-if="list.length">
            <div class="h-10 text-xs hidden items-center pl-[106px] gap-x-4 lg:flex">
                <div class="lg:w-60">Name</div>
                <div>Users</div>
            </div>

            <org-unit-row
                v-for="(item, index) in list"
                :modelValue="list[index]"
                :key="list[index].id"
                @deleted="removeFromList($event)"
            ></org-unit-row>
        </div>

        <div v-if="list.length && fetchParams.cursor" class="mt-8">
            <vx-button
                @click="loadData()"
                color="muted-dark"
                size="xl"
                data-test="load-more-btn"
                class="grow-0"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
        </div>

        <org-unit-create-modal v-model:visible="showCreateModal" :teamId="teamId" @created="handleCreated" />
    </div>
</template>

<script setup>
import {
    faCircleChevronDown,
    faCircleChevronUp,
    faCirclePlus,
    faCircleQuestion,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton } from '@voxie/frontend-components';
import { onMounted, ref } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { orgUnitsClient } from '~/services';
import OrgUnitControls from './OrgUnitControls.vue';
import OrgUnitsOverview from './OrgUnitsOverview.vue';
import OrgUnitRow from './OrgUnitRow.vue';
import OrgUnitCreateModal from '../form/OrgUnitCreateModal.vue';

const showOverview = ref(true);
const showCreateModal = ref(false);

const teamId = Spark.state.currentTeam.id;
const isAdminOrOwner = Spark.isAdminOrOwner;

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => ({
    per_page: 10,
    cursor: null,
    sort_field: 'id',
    sort_dir: 'desc',
    query: '',
});

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

onMounted(() => {
    resetFilters();
});

const loadData = () => {
    fetchRecords(orgUnitsClient.listOrgUnits(teamId, { ...fetchParams.value }));
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id !== itemId);

    if (!list.value.length) {
        loadData();
    }
};

const handleCreated = (newOrgUnit) => {
    list.value.unshift(newOrgUnit);
};
</script>
